import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PaymentService, PaymentPromisePageDto } from '../payment.service';
import { CreatePromiseCommand, PaymentInstallmentDto, PaymentPlanCommand, PaymentPlanDto, PaymentPromiseDto, PaymentPromiseStatus, InstallmentStatus } from '../models/payment.model';

interface PromiseApiDto { id?: string | number; promiseId?: string | number; caseId?: string | number; caseReference?: string; debtorId?: string | number; debtorName?: string; promiseDate?: string; date?: string; amount?: number; promiseAmount?: number; currency?: string; channel?: string; status?: string; createdByAgentId?: string; createdByAgentName?: string; createdAt?: string; notes?: string; relatedActionId?: string; [key: string]: unknown; }
interface Page<T> { content?: T[]; items?: T[]; totalElements?: number; total?: number; }
interface PlanApiDto { id?: string | number; planId?: string | number; caseId?: string | number; totalAmount?: number; amount?: number; installmentCount?: number; frequency?: string; firstInstallmentDate?: string; firstDueDate?: string; status?: string; derogation?: boolean; derogationReason?: string; installments?: unknown[]; [key: string]: unknown; }

@Injectable()
export class PaymentApiService extends PaymentService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1`;

  getPromises(caseId?: string): Observable<PaymentPromisePageDto> {
    const url = caseId ? `${this.base}/cases/${encodeURIComponent(caseId)}/promises` : `${this.base}/promises`;
    return this.http.get<Page<PromiseApiDto> | PromiseApiDto[]>(url).pipe(
      map(response => {
        const page = Array.isArray(response) ? { content: response } : response;
        return { items: (page.content ?? page.items ?? []).map(mapPromiseDto) };
      }),
    );
  }

  createPromise(command: CreatePromiseCommand): Observable<PaymentPromiseDto> {
    return this.http.post<PromiseApiDto>(`${this.base}/cases/${encodeURIComponent(command.caseId)}/promises`, {
      amount: command.promiseAmount,
      promiseAmount: command.promiseAmount,
      promiseDate: command.promiseDate,
      currency: command.currency,
      channel: command.channel,
      notes: command.notes,
      derogation: command.derogation,
      derogationReason: command.derogationReason,
      historicalRegularization: command.historicalRegularization,
    }).pipe(map(mapPromiseDto));
  }

  getPaymentPlan(caseId: string): Observable<PaymentPlanDto | null> {
    return this.http.get<Page<PlanApiDto> | PlanApiDto[]>(`${this.base}/cases/${encodeURIComponent(caseId)}/payment-plans`).pipe(
      map(response => {
        const values = Array.isArray(response) ? response : (response.content ?? response.items ?? []);
        return values.length ? mapPlanDto(values[0]) : null;
      }),
    );
  }

  createPaymentPlan(command: PaymentPlanCommand): Observable<PaymentPlanDto> {
    return this.http.post<PlanApiDto>(`${this.base}/cases/${encodeURIComponent(command.caseId)}/payment-plans`, {
      totalAmount: command.totalAmount,
      installmentCount: command.installmentCount,
      firstDueDate: command.firstInstallmentDate,
      firstInstallmentDate: command.firstInstallmentDate,
      frequency: command.frequency,
      derogation: command.derogation ?? false,
      derogationReason: command.derogationReason,
      installments: command.installments,
    }).pipe(map(mapPlanDto));
  }
}

export function mapPromiseDto(dto: PromiseApiDto): PaymentPromiseDto {
  return {
    promiseId: String(dto.promiseId ?? dto.id ?? ''),
    caseId: String(dto.caseId ?? ''),
    caseReference: String(dto.caseReference ?? ''),
    debtorId: String(dto.debtorId ?? ''),
    debtorName: String(dto.debtorName ?? ''),
    promiseDate: String(dto.promiseDate ?? dto.date ?? ''),
    promiseAmount: numberValue(dto.promiseAmount ?? dto.amount),
    currency: dto.currency,
    channel: dto.channel,
    status: normalizeStatus(dto.status),
    createdByAgentId: String(dto.createdByAgentId ?? ''),
    createdByAgentName: String(dto.createdByAgentName ?? ''),
    createdAt: String(dto.createdAt ?? ''),
    notes: dto.notes,
    relatedActionId: dto.relatedActionId,
  };
}

function numberValue(value: unknown): number { return typeof value === 'number' && Number.isFinite(value) ? value : 0; }
function normalizeStatus(value: unknown): PaymentPromiseStatus {
  const status = String(value ?? 'ACTIVE').toUpperCase();
  return (['ACTIVE', 'KEPT', 'BROKEN', 'CANCELLED'] as string[]).includes(status) ? status as PaymentPromiseStatus : 'ACTIVE';
}

export function mapPlanDto(dto: PlanApiDto): PaymentPlanDto {
  return {
    caseId: String(dto.caseId ?? ''),
    totalAmount: numberValue(dto.totalAmount ?? dto.amount),
    installmentCount: numberValue(dto.installmentCount ?? dto.installments?.length),
    firstInstallmentDate: String(dto.firstInstallmentDate ?? dto.firstDueDate ?? ''),
    frequency: normalizeFrequency(dto.frequency),
    derogation: Boolean(dto.derogation),
    derogationReason: dto.derogationReason == null ? undefined : String(dto.derogationReason),
    installments: Array.isArray(dto.installments) ? dto.installments.map(mapInstallment) : [],
    status: normalizePlanStatus(dto.status),
  };
}

function mapInstallment(value: unknown): PaymentInstallmentDto {
  const dto = (value ?? {}) as Record<string, unknown>;
  return {
    installmentNumber: numberValue(dto['installmentNumber'] ?? dto['number']),
    dueDate: String(dto['dueDate'] ?? ''),
    amount: numberValue(dto['amount']),
    status: normalizeInstallmentStatus(dto['status']),
    paidDate: dto['paidDate'] == null ? undefined : String(dto['paidDate']),
    paidAmount: typeof dto['paidAmount'] === 'number' ? dto['paidAmount'] : undefined,
  };
}

function normalizeFrequency(value: unknown): PaymentPlanCommand['frequency'] {
  const frequency = String(value ?? 'MONTHLY').toUpperCase();
  return (['MONTHLY', 'WEEKLY', 'BIMONTHLY'] as string[]).includes(frequency) ? frequency as PaymentPlanCommand['frequency'] : 'MONTHLY';
}
function normalizePlanStatus(value: unknown): PaymentPlanDto['status'] {
  const status = String(value ?? 'ACTIVE').toUpperCase();
  return (['DRAFT', 'ACTIVE', 'COMPLETED', 'BROKEN', 'CANCELLED'] as string[]).includes(status) ? status as PaymentPlanDto['status'] : 'ACTIVE';
}
function normalizeInstallmentStatus(value: unknown): InstallmentStatus {
  const status = String(value ?? 'SCHEDULED').toUpperCase();
  return (['SCHEDULED', 'PAID', 'OVERDUE', 'CANCELLED'] as string[]).includes(status) ? status as InstallmentStatus : 'SCHEDULED';
}
